import axiosInstance from './axiosInstance';

export interface OAuthDecision {
  outcome: 'accepted' | 'rejected';
  /** The issuer address at which the browser continues, for either outcome. */
  redirect_to: string;
}

export interface DelegablePermission {
  key: string;
  /** The permission's name for people, such as "Read logs". */
  name: string;
  description: string;
}

export interface DelegablePermissions {
  resource_type: string;
  /** The resource type's name for people, such as "Block". */
  name: string;
  /** The type whose resources' children may be shared together, such as "projects". */
  parent_resource_type: string | null;
  permissions: DelegablePermission[];
}

export interface OAuthLoginRequest {
  client_id: string;
  /** Empty when the application was registered without a name. */
  client_name: string;
}

export interface OAuthConsentRequest {
  client_id: string;
  client_name: string;
  api_audience: { name: string; audience_uri: string } | null;
  scopes: string[];
  delegable_permissions: DelegablePermissions[];
}

/** What a selection covers: one resource, every current and future resource of the type, or
 * every current and future child of one parent. */
export type ResourceSelector =
  | { kind: 'resource'; resource_id: string }
  | { kind: 'all' }
  | { kind: 'children'; parent_resource_type: string; parent_resource_id: string };

/** Something the person may share, with the keys they may share on it. */
export interface DelegableResource {
  resource_type: string;
  selector: ResourceSelector;
  /** The resource's name; the parent's for its children; empty for all resources. */
  name: string;
  /** A single resource's parent. */
  parent: { resource_type: string; resource_id: string } | null;
  /** May be empty for all resources or a parent's children, which need the same access held. */
  permission_keys: string[];
}

export interface ResourceSelection {
  resource_type: string;
  selector: ResourceSelector;
  permission_keys: string[];
}

export interface DelegableResourcesPage {
  resources: DelegableResource[];
  /** The next page's address, followed as given. */
  next: string | null;
}

export interface Organisation {
  organisation_id: string;
  /** Organisations created before names were required may have none. */
  name: string | null;
}

export type ConsentAnswer =
  | {
      action: 'accept';
      organisation_id: string;
      resources: ResourceSelection[];
    }
  | { action: 'reject' };

const jsonApi = { headers: { Accept: 'application/vnd.api+json' } };

function path(resource: string, challenge: string, rest = ''): string {
  return `/${resource}/${encodeURIComponent(challenge)}${rest}`;
}

const oauthService = {
  /** Names the application that asks the person to sign in; needs no session. */
  fetchLoginRequest: async (challenge: string): Promise<OAuthLoginRequest> => {
    const response = await axiosInstance.get(path('oauthLoginRequests', challenge), jsonApi);
    return response.data.data.attributes;
  },

  /** Completes or cancels an application's sign-in for the current session. */
  respondToLoginRequest: async (
    challenge: string,
    action: 'accept' | 'reject'
  ): Promise<OAuthDecision> => {
    const response = await axiosInstance.patch(
      path('oauthLoginRequests', challenge),
      { action },
      jsonApi
    );
    return response.data.data.attributes;
  },

  fetchConsentRequest: async (challenge: string): Promise<OAuthConsentRequest> => {
    const response = await axiosInstance.get(path('oauthConsentRequests', challenge), jsonApi);
    return response.data.data.attributes;
  },

  /** One page of what the person may share of a type in the organisation they chose: all of the
   * type first, then each parent's children, then single resources. */
  fetchDelegableResources: async (
    challenge: string,
    organisationId: string,
    resourceType: string,
    next?: string
  ): Promise<DelegableResourcesPage> => {
    const response = next
      ? await axiosInstance.get(next, jsonApi)
      : await axiosInstance.get(path('oauthConsentRequests', challenge, '/delegableResources'), {
          ...jsonApi,
          params: { organisation_id: organisationId, resource_type: resourceType },
        });
    const href: string | undefined = response.data.links?.next?.href;
    return {
      resources: response.data.data.map(
        (resource: { attributes: DelegableResource }) => resource.attributes
      ),
      next: href ? new URL(href, axiosInstance.defaults.baseURL).toString() : null,
    };
  },

  respondToConsentRequest: async (
    challenge: string,
    answer: ConsentAnswer
  ): Promise<OAuthDecision> => {
    const response = await axiosInstance.patch(
      path('oauthConsentRequests', challenge),
      answer,
      jsonApi
    );
    return response.data.data.attributes;
  },

  /** The organisations the person may connect an application to. */
  fetchOrganisations: async (): Promise<Organisation[]> => {
    const response = await axiosInstance.get('/organisations', jsonApi);
    return response.data.data.map(
      (organisation: { id: string; attributes: { name?: string | null } }) => ({
        organisation_id: organisation.id,
        name: organisation.attributes.name ?? null,
      })
    );
  },
};

/** The response status, or undefined when the request never got an answer. */
export function errorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The backend's error detail, or `fallback` for anything else, such as a proxy error page. */
export function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { errors?: Array<{ detail?: unknown }> } } })
    ?.response?.data?.errors?.[0]?.detail;
  return typeof detail === 'string' && detail !== '' ? detail : fallback;
}

export default oauthService;
